import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import { Loader2, ChevronLeft, Sparkles, CheckCircle2, Circle } from "lucide-react";

function bold(text) {
  const html = text
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-foreground">$1</strong>')
    .replace(/(^|[^*])\*(?!\s)([^*\n]+?)\*(?!\*)/g, '$1<em>$2</em>');
  return { __html: DOMPurify.sanitize(html, { ALLOWED_TAGS: ["strong", "em"], ALLOWED_ATTR: ["class"] }) };
}

// Split markdown into sections by top-level "## " headings. Content before the
// first "## " (e.g. the "# Title" + intro) becomes the "Introduction" preface.
function parseSections(md) {
  const lines = md.split("\n");
  const sections = [];
  let preface = [];
  let cur = null;
  for (const line of lines) {
    if (line.trim().startsWith("## ")) {
      if (cur) sections.push(cur);
      cur = { title: line.trim().slice(3).trim(), body: [] };
    } else if (cur) {
      cur.body.push(line);
    } else {
      preface.push(line);
    }
  }
  if (cur) sections.push(cur);
  return { preface: preface.join("\n"), sections };
}

function renderContent(md) {
  return md.split("\n").map((line, i) => {
    const trimmed = line.trim();
    if (trimmed.startsWith("### ")) return <h3 key={i} className="font-heading font-bold text-lg mt-6 mb-2">{trimmed.slice(4)}</h3>;
    if (trimmed.startsWith("## ")) return <h2 key={i} className="font-heading font-bold text-2xl mt-8 mb-3">{trimmed.slice(3)}</h2>;
    if (trimmed.startsWith("# ")) return <h1 key={i} className="font-heading font-black text-3xl mt-4 mb-4 tracking-tight">{trimmed.slice(2)}</h1>;
    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      return <li key={i} className="ml-6 list-disc text-muted-foreground leading-relaxed mb-1" dangerouslySetInnerHTML={bold(trimmed.slice(2))} />;
    }
    if (!trimmed) return <div key={i} className="h-2" />;
    return <p key={i} className="text-muted-foreground leading-relaxed mb-3" dangerouslySetInnerHTML={bold(trimmed)} />;
  });
}

export default function CourseDetail() {
  const { id } = useParams();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["course", id],
    queryFn: async () => (await api.get(`/courses/${id}`)).data,
  });
  const course = data?.course;

  const { data: progressData } = useQuery({
    queryKey: ["course-progress", id],
    queryFn: async () => (await api.get(`/courses/${id}/progress`)).data,
  });
  const completed = progressData?.completed || [];

  const toggleMut = useMutation({
    mutationFn: async ({ section, done }) =>
      (await api.put(`/courses/${id}/progress`, { section, completed: done })).data,
    onMutate: async ({ section, done }) => {
      await qc.cancelQueries({ queryKey: ["course-progress", id] });
      const prev = qc.getQueryData(["course-progress", id]);
      qc.setQueryData(["course-progress", id], (old) => {
        const set = new Set((old?.completed) || []);
        done ? set.add(section) : set.delete(section);
        return { completed: [...set] };
      });
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(["course-progress", id], ctx.prev),
  });

  const { preface, sections } = useMemo(
    () => parseSections(course?.content || ""),
    [course?.content]
  );

  if (isLoading || !course)
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const total = sections.length;
  const doneCount = sections.filter((s) => completed.includes(s.title)).length;
  const pct = total ? Math.round((doneCount / total) * 100) : 0;

  return (
    <div className="max-w-3xl mx-auto" data-testid="course-detail">
      <Link to="/courses" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ChevronLeft className="w-4 h-4" /> Back to Courses
      </Link>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs font-mono uppercase tracking-wider px-2 py-1 rounded bg-secondary">{course.category}</span>
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{course.difficulty}</span>
        {course.ai_generated && (
          <span className="inline-flex items-center gap-1 text-xs font-mono px-2 py-1 rounded bg-primary/10 text-primary">
            <Sparkles className="w-3 h-3" /> AI Generated
          </span>
        )}
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-4">{course.title}</h1>
      <p className="text-muted-foreground text-lg mb-6 leading-relaxed">{course.description}</p>

      {total > 0 && (
        <div className="p-5 rounded-md border border-border bg-card mb-6" data-testid="course-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="font-heading font-bold">Your Progress</span>
            <span className="font-mono text-sm text-muted-foreground" data-testid="progress-count">{doneCount} of {total} complete</span>
          </div>
          <div className="h-2.5 rounded-full bg-secondary overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-500"
              style={{ width: `${pct}%` }}
              data-testid="progress-bar"
            />
          </div>
          {pct === 100 && (
            <p className="text-sm text-success font-medium mt-3 flex items-center gap-1.5" data-testid="course-complete-msg">
              <CheckCircle2 className="w-4 h-4" /> Course complete — great work!
            </p>
          )}
        </div>
      )}

      {preface.trim() && (
        <div className="p-6 lg:p-8 rounded-md border border-border bg-card mb-4">
          {renderContent(preface)}
        </div>
      )}

      <div className="space-y-4">
        {sections.map((sec) => {
          const done = completed.includes(sec.title);
          return (
            <div
              key={sec.title}
              className={`rounded-md border bg-card transition-colors ${done ? "border-success/40" : "border-border"}`}
              data-testid={`section-${sec.title.replace(/[^a-zA-Z0-9]+/g, "-")}`}
            >
              <div className="flex items-start justify-between gap-3 p-6 lg:p-8 pb-0">
                <h2 className="font-heading font-bold text-2xl">{sec.title}</h2>
                <button
                  onClick={() => toggleMut.mutate({ section: sec.title, done: !done })}
                  className={`shrink-0 mt-1 inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${done ? "text-success" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid={`toggle-${sec.title.replace(/[^a-zA-Z0-9]+/g, "-")}`}
                >
                  {done ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                  <span className="hidden sm:inline">{done ? "Completed" : "Mark complete"}</span>
                </button>
              </div>
              <div className="px-6 lg:px-8 pb-6 lg:pb-8">
                {renderContent(sec.body.join("\n"))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
